import type { ImportLimits, ImportedFile } from "../types";
import { randomUuid } from "./browser-crypto";

type WorkerResponse = { id: string; result?: ImportedFile; error?: string };

export class Importer {
  private worker = new Worker(new URL("../workers/import.worker.ts", import.meta.url), { type: "module" });
  private pending = new Map<string, { resolve: (result: ImportedFile) => void; reject: (error: Error) => void }>();

  constructor() {
    this.worker.onmessage = (event: MessageEvent<WorkerResponse>) => {
      const pending = this.pending.get(event.data.id); if (!pending) return;
      this.pending.delete(event.data.id);
      if (event.data.error) pending.reject(new Error(event.data.error)); else pending.resolve(event.data.result!);
    };
  }

  parse(file: File, limits: ImportLimits): Promise<ImportedFile> {
    const id = randomUuid();
    return new Promise((resolve, reject) => {
      this.pending.set(id, { resolve, reject });
      this.worker.postMessage({ id, file, limits });
    });
  }

  close() { this.worker.terminate(); }
}

export async function probeImage(file: Blob, maxDimension: number, maxPixels: number) {
  const bitmap = await createImageBitmap(file);
  const result = { width: bitmap.width, height: bitmap.height };
  bitmap.close();
  if (result.width > maxDimension || result.height > maxDimension || result.width * result.height > maxPixels) {
    throw new Error("Decoded image exceeds the configured dimension or megapixel limit.");
  }
  return result;
}

export async function probeVideo(file: File): Promise<{ duration: number; width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    const url = URL.createObjectURL(file);
    video.preload = "metadata";
    video.onloadedmetadata = () => {
      const result = { duration: video.duration, width: video.videoWidth, height: video.videoHeight };
      URL.revokeObjectURL(url);
      if (!Number.isFinite(result.duration) || !result.width || !result.height) reject(new Error("The browser could not read this video's codec or metadata."));
      else resolve(result);
    };
    video.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Unsupported video container or codec. Use browser-compatible H.264/AAC MP4, M4V, or WebM.")); };
    video.src = url;
  });
}

export async function probeAudio(file: File): Promise<{ duration: number }> {
  return new Promise((resolve, reject) => {
    const audio = document.createElement("audio");
    const url = URL.createObjectURL(file);
    audio.preload = "metadata";
    audio.onloadedmetadata = () => {
      const duration = audio.duration; URL.revokeObjectURL(url);
      if (!Number.isFinite(duration) || duration <= 0) reject(new Error("The browser could not read this audio file's codec or metadata."));
      else resolve({ duration });
    };
    audio.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Unsupported audio container or codec. Use browser-compatible MP3, WAV, OGG, M4A, AAC, FLAC, OPUS, AIFF, or audio WebM.")); };
    audio.src = url;
  });
}
